'use client';

import { useEffect, useState } from 'react';

import { SEARCH_RESULT_CONTAINER_ID } from '@/constants/index';
import { updatePageTitle } from '@/services/search/utils';
import { useSearchContext } from '@/src/services/search/SearchProvider';
import { GRSFacetConfigProperties } from '@/types/contentStack';
import { Box } from '@mui/material';
import { Breadcrumbs } from '@nxt-costco-com/forge-components';
import {
    ContentStackEntryDataProps,
    normalizeCategoryLink,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';

import { SkipToResultsLink } from './SkipToResultsLink';

import { BreadCrumbContainerStyle } from './styles';

export function CategoryBreadCrumbClient({
    ariaLabel,
    skipLinkText,
    initialValues = [],
    categoryUrlPath,
    displaySearchFilters,
    publishedCategories,
    grsFacetConfig,
    facetConfig,
}: {
    categoryUrlPath: string | undefined;
    ariaLabel: string;
    skipLinkText: string;
    initialValues?: Array<{ name: string; link: string }>;
    displaySearchFilters: boolean;
    publishedCategories: string[];
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
    facetConfig?: ContentStackEntryDataProps | undefined;
}) {
    const { queryParams: searchParams } = useQueryParams();
    const { searchResult, searchService, isSearchResultsLoading } =
        useSearchContext();
    const [breadcrumbs, setBreadcrumbs] =
        useState<Array<{ name: string; link: string }>>(initialValues);

    useEffect(() => {
        if (!searchResult || !searchResult.breadcrumb) {
            return;
        }

        // Don't lose the homepage breadcrumb
        const homeBreadcrumb = initialValues.find(item => item.link === '/');
        const newCrumbs = [];

        if (homeBreadcrumb) {
            newCrumbs.push(homeBreadcrumb);
        }

        if (Array.isArray(searchResult?.breadcrumb)) {
            newCrumbs.push(
                ...searchResult.breadcrumb.map(
                    (breadCrumb: {
                        name: string;
                        url: string;
                        fq: string;
                    }) => ({
                        name: breadCrumb.name,
                        link: normalizeCategoryLink(
                            categoryUrlPath,
                            breadCrumb.url,
                            publishedCategories,
                        ),
                    }),
                ),
            );
        }

        setBreadcrumbs(newCrumbs);
    }, [searchResult, categoryUrlPath, publishedCategories, initialValues]);

    useEffect(() => {
        if (document) {
            // Set the page title to the last breadcrumb
            if (breadcrumbs?.[breadcrumbs.length - 1]?.name) {
                updatePageTitle({
                    searchParams,
                    searchResult,
                    pageTitle: breadcrumbs[breadcrumbs.length - 1]!.name,
                    searchService,
                    grsFacetConfig,
                    facetConfig,
                });
            }
        }
    }, [
        searchParams,
        searchResult,
        breadcrumbs,
        grsFacetConfig,
        searchService,
        facetConfig,
    ]);

    if (isSearchResultsLoading || !breadcrumbs) {
        return null;
    }

    return (
        <Box sx={BreadCrumbContainerStyle}>
            <script type="application/ld+json">
                {JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: breadcrumbs.map((item, index) => {
                        const domain =
                            'https://' + window.location.hostname + item.link;
                        const sanitizedDomain = String(domain || '').replace(
                            /[<>"'&]/g,
                            '',
                        );

                        return {
                            '@type': 'ListItem',
                            position: index + 1,
                            name: item.name,
                            item: sanitizedDomain,
                        };
                    }),
                })}
            </script>
            <Breadcrumbs
                ariaLabel={ariaLabel}
                separator="/"
                breadcrumbElements={breadcrumbs}
            />
            {displaySearchFilters && (
                // Do not display skip-to if there are not filters to skip over
                <SkipToResultsLink
                    skipLinkText={skipLinkText}
                    targetId={SEARCH_RESULT_CONTAINER_ID}
                />
            )}
        </Box>
    );
}
